import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CalendarCheck,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Minimize2,
  Users,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import {
  type OnboardingProgress,
  type OnboardingStepId,
  type SubItemStatus,
} from '@/hooks/useOnboardingProgress';
import { logger } from '@/lib/logger';
import { OnboardingCelebrationModal } from './OnboardingCelebrationModal';
import { OnboardingWelcomeModal } from './OnboardingWelcomeModal';

interface OnboardingWizardProps {
  progress: OnboardingProgress;
}

interface WizardSubItem {
  key: keyof SubItemStatus;
  label: string;
  optional?: boolean;
  pageTarget?: string;  // data-onboarding attr of the element on the step's page
  pageHint?: string;    // speech bubble text for in-page spotlight
  bubblePosition?: 'right' | 'below' | 'left-of-modal'; // override default positioning
}

interface WizardStepDef {
  id: OnboardingStepId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  target?: string;
  spotlightHint?: string;
  navBubblePosition?: 'below' | 'left-of-panel';
  subItems: WizardSubItem[];
}

const WIZARD_STEPS: WizardStepDef[] = [
  {
    id: 'configure-business',
    label: 'Configurar tu negocio',
    description: 'Ingresa los datos básicos y los horarios de atención de tu negocio.',
    icon: Building2,
    route: '/settings?section=salon',
    target: 'nav-settings',
    spotlightHint: 'Da clic y entra a la configuración de tu negocio.',
    subItems: [
      { key: 'salonName',    label: 'Nombre del negocio', pageTarget: 'field-salon-name',          pageHint: 'Escribe el nombre de tu negocio tal como lo verán tus clientes ✏️' },
      { key: 'salonLogo',    label: 'Subir logo',         pageTarget: 'field-salon-logo',          pageHint: 'Sube tu logo. Te recomendamos que tenga un fondo sólido 😊' },
      { key: 'salonPhone',   label: 'Teléfono',           pageTarget: 'field-salon-phone',         pageHint: 'Escribe tu número de WhatsApp o teléfono — se guarda solo cuando es válido 📱' },
      { key: 'salonEmail',   label: 'Email de contacto',  pageTarget: 'field-salon-email',         pageHint: 'Ingresa el email de tu negocio — se guarda automáticamente al salir del campo ✉️' },
      { key: 'salonAddress', label: 'Dirección',          pageTarget: 'field-salon-address-group', pageHint: 'Ingresa tu dirección y presiona Guardar cambios para continuar 📍' },
      { key: 'salonHours',   label: 'Horarios de atención', pageTarget: 'field-salon-hours',       pageHint: 'Ajusta los horarios de cada día y presiona Guardar para terminar el Paso 1 🕐' },
    ],
  },
  {
    id: 'configure-services',
    label: 'Configurar tus servicios',
    description: 'Crea las categorías y servicios que ofrecerás a tus clientes.',
    icon: ClipboardList,
    route: '/services',
    target: 'nav-services',
    spotlightHint: 'Da clic en Servicios para crear tus categorías y servicios 🏷️',
    subItems: [
      { key: 'hasCategories', label: 'Crear categoría', pageTarget: 'btn-nueva-categoria', pageHint: 'Haz clic aquí para crear tu primera categoría de servicios 🏷️' },
      { key: 'hasServices',   label: 'Crear servicio',  pageTarget: 'btn-nuevo-servicio',  pageHint: '¡Genial! Ahora crea tu primer servicio y asígnale la categoría ✨' },
    ],
  },
  {
    id: 'create-professional',
    label: 'Agrega un profesional',
    description: 'Agrega un profesional y asígnale los servicios que realiza.',
    icon: UserRound,
    route: '/professionals',
    target: 'nav-professionals',
    spotlightHint: 'Da clic en Profesionales para crear tu equipo 👤',
    subItems: [
      { key: 'hasProfessionals', label: 'Rellenar formulario', pageTarget: 'modal-professional-form', pageHint: '✏️ Escribe el nombre, asigna los servicios y presiona Guardar 💼', bubblePosition: 'left-of-modal' },
      { key: 'hasProfessionals',        label: 'Crear profesional', pageTarget: 'btn-nuevo-profesional', pageHint: 'Haz clic aquí para crear tu primer profesional 👤' },
      { key: 'hasProfessionalServices', label: 'Asignar servicios', optional: true },
    ],
  },
  {
    id: 'create-client',
    label: 'Agrega tu primer cliente',
    description: 'Registra tu primer cliente en el sistema.',
    icon: Users,
    route: '/clients',
    target: 'nav-clients',
    spotlightHint: 'Da clic en Clientes 👥',
    navBubblePosition: 'left-of-panel',
    subItems: [
      { key: 'hasClients', label: 'Crear cliente', pageTarget: 'modal-client-form', pageHint: 'Llena todos los campos y da clic en Crear cliente 👤', bubblePosition: 'left-of-modal' },
      { key: 'hasClients', label: 'Crear cliente', pageTarget: 'btn-nuevo-cliente', pageHint: 'Haz clic aquí para agregar tu primer cliente 👤' },
    ],
  },
  {
    id: 'create-appointment',
    label: 'Crea tu primera cita',
    description: 'Agenda tu primera cita. Tu negocio está listo para operar.',
    icon: CalendarCheck,
    route: '/appointments',
    target: 'nav-appointments',
    spotlightHint: 'Da clic en Agenda y crea tu primera cita. 🗓️',
    navBubblePosition: 'below',
    subItems: [
      { key: 'hasAppointments', pageTarget: 'btn-nueva-cita', pageHint: '¡Último paso! Haz clic aquí y agenda tu primera cita 🎉' },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ── Inject keyframe CSS once ──────────────────────────────────────────────────
function ensureSpotlightStyles() {
  if (document.getElementById('_ms_sp_kf')) return;
  const s = document.createElement('style');
  s.id = '_ms_sp_kf';
  s.textContent = `
    @keyframes _ms_paso_bounce {
      0%,100% { transform: translateX(0); }
      50%      { transform: translateX(8px); }
    }
  `;
  document.head.appendChild(s);
}

// ── Success ding (Web Audio API) ──────────────────────────────────────────────
function playSubItemSound() {
  try {
    const ctx = new AudioContext();
    const notes: [number, number][] = [[659, 0], [784, 0.14]];
    for (const [freq, delay] of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + delay);
      gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + delay + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.45);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.5);
    }
  } catch {
    // silent fallback
  }
}

// ── Spotlight state shape ─────────────────────────────────────────────────────
interface TargetRect {
  top: number; bottom: number; left: number; right: number;
  width: number; height: number;
}
interface SpotlightState {
  stepNumber: number;
  hint: string;
  targetRect: TargetRect;
  targetSelector: string; // for resize tracking
  bubblePosition: 'right' | 'below' | 'left-of-modal' | 'left-of-panel';
}

// ── Main component ────────────────────────────────────────────────────────────
export function OnboardingWizard({ progress }: OnboardingWizardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [minimized, setMinimized] = useState(false);
  const [expandedStepId, setExpandedStepId] = useState<OnboardingStepId | null>(
    progress.currentStep?.id ?? null,
  );
  const [spotlight, setSpotlight] = useState<SpotlightState | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // refs to each step row button — used to align PASO N▶ vertically
  const stepRowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const prevSubItemRef           = useRef<SubItemStatus | null>(null);
  const previousCurrentStepIdRef = useRef<OnboardingStepId | null>(progress.currentStep?.id ?? null);
  const lastSpotlightedStepRef   = useRef<string | null>(null);
  const finalizingRef            = useRef(false);
  const backfillRef              = useRef(false);

  // ── Keep accordion in sync with current step ──────────────────────────────
  useEffect(() => {
    if (progress.currentStep?.id) setExpandedStepId(progress.currentStep.id);
  }, [progress.currentStep?.id]);

  // ── Spotlight: activate / dismiss ─────────────────────────────────────────
  const dismissSpotlight = useCallback(() => {
    setSpotlight(null);
  }, []);

  const activateSpotlight = useCallback((stepDef: WizardStepDef, stepNumber: number) => {
    if (!stepDef.target) return;
    ensureSpotlightStyles();

    const selector = `[data-onboarding="${stepDef.target}"]`;
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    setSpotlight({
      stepNumber,
      hint: stepDef.spotlightHint ?? stepDef.label,
      targetSelector: selector,
      bubblePosition: stepDef.navBubblePosition ?? 'below',
      targetRect: {
        top: rect.top, bottom: rect.bottom,
        left: rect.left, right: rect.right,
        width: rect.width, height: rect.height,
      },
    });
  }, []);

  // ── Dismiss spotlight on navigation ──────────────────────────────────────
  useEffect(() => {
    dismissSpotlight();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // ── Dismiss / re-arm spotlight on minimize toggle ─────────────────────────
  useEffect(() => {
    if (minimized) {
      dismissSpotlight();
    } else {
      // Allow spotlight to re-fire when panel re-opens
      lastSpotlightedStepRef.current = null;
    }
  }, [minimized, dismissSpotlight]);

  // ── Auto-fire NAV spotlight for current step (only when NOT on step's page) ──
  useEffect(() => {
    if (minimized || !progress.isActive) return;
    const stepId = progress.currentStep?.id;
    if (!stepId || stepId === lastSpotlightedStepRef.current) return;

    const stepDef = WIZARD_STEPS.find(s => s.id === stepId);
    if (!stepDef?.target) return;

    // Don't fire nav spotlight if already on the step's page
    const stepBasePath = stepDef.route.split('?')[0];
    if (location.pathname.startsWith(stepBasePath)) return;

    lastSpotlightedStepRef.current = stepId;
    const stepNumber = WIZARD_STEPS.findIndex(s => s.id === stepId) + 1;
    const t = setTimeout(() => activateSpotlight(stepDef, stepNumber), 500);
    return () => clearTimeout(t);
  }, [progress.currentStep?.id, minimized, progress.isActive, activateSpotlight, location.pathname]);

  // ── In-page spotlight: guides through sub-items on the step's page ────────
  useEffect(() => {
    if (minimized || !progress.isActive) return;
    const stepId = progress.currentStep?.id;
    if (!stepId) return;

    const stepDef = WIZARD_STEPS.find(s => s.id === stepId);
    if (!stepDef) return;

    // Only run when on the step's page
    const stepBasePath = stepDef.route.split('?')[0];
    if (!location.pathname.startsWith(stepBasePath)) return;

    const tryActivate = () => {
      // Find first pending sub-item with a DOM element that has real dimensions
      const nextSubItem = stepDef.subItems.find(si => {
        if (!si.pageTarget || progress.subItemStatus[si.key]) return false;
        const el = document.querySelector<HTMLElement>(`[data-onboarding="${si.pageTarget}"]`);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.width > 0 || r.height > 0; // element must be rendered
      });
      if (!nextSubItem?.pageTarget) return false;

      const selector = `[data-onboarding="${nextSubItem.pageTarget}"]`;
      const el = document.querySelector<HTMLElement>(selector);
      if (!el) return false;

      ensureSpotlightStyles();
      const rect = el.getBoundingClientRect();
      const stepNumber = WIZARD_STEPS.findIndex(s => s.id === stepId) + 1;
      setSpotlight({
        stepNumber,
        hint: nextSubItem.pageHint ?? nextSubItem.label,
        targetSelector: selector,
        bubblePosition: nextSubItem.bubblePosition ?? 'right',
        targetRect: {
          top: rect.top, bottom: rect.bottom,
          left: rect.left, right: rect.right,
          width: rect.width, height: rect.height,
        },
      });
      return true;
    };

    // Try immediately (works when DOM is already loaded, e.g. sub-item completion)
    if (!tryActivate()) {
      // Content may still be loading after navigation — retry after render
      const t = setTimeout(tryActivate, 700);
      return () => clearTimeout(t);
    }
  }, [
    progress.subItemStatus,
    progress.currentStep?.id,
    location.pathname,
    location.search,
    minimized,
    progress.isActive,
  ]);

  // ── MutationObserver: detecta cuando el modal de profesional abre ────────
  useEffect(() => {
    if (minimized || !progress.isActive) return;
    if (progress.currentStep?.id !== 'create-professional') return;
    if (progress.subItemStatus.hasProfessionals) return;
    if (!location.pathname.startsWith('/professionals')) return;

    const tryModalSpotlight = () => {
      const modal = document.querySelector<HTMLElement>('[data-onboarding="modal-professional-form"]');
      if (!modal) return;
      const rect = modal.getBoundingClientRect();
      if (rect.width === 0) return;
      ensureSpotlightStyles();
      const modalStep = document.querySelector<HTMLElement>('[data-modal-step]')?.getAttribute('data-modal-step');
      const hint = modalStep === 'services'
        ? '✅ Selecciona los servicios y presiona Guardar 💼'
        : '✏️ Escribe el nombre del profesional y presiona Enter';
      const stepNumber = WIZARD_STEPS.findIndex(s => s.id === 'create-professional') + 1;
      setSpotlight({
        stepNumber,
        hint,
        targetSelector: '[data-onboarding="modal-professional-form"]',
        bubblePosition: 'left-of-modal',
        targetRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height },
      });
    };

    const observer = new MutationObserver(tryModalSpotlight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-modal-step'] });
    tryModalSpotlight(); // check si ya está abierto
    return () => observer.disconnect();
  }, [
    progress.currentStep?.id,
    progress.subItemStatus.hasProfessionals,
    minimized,
    progress.isActive,
    location.pathname,
  ]);

  // ── MutationObserver: detecta cuando el modal de cliente abre ─────────────
  useEffect(() => {
    if (minimized || !progress.isActive) return;
    if (progress.currentStep?.id !== 'create-client') return;
    if (progress.subItemStatus.hasClients) return;
    if (!location.pathname.startsWith('/clients')) return;

    const tryClientModalSpotlight = () => {
      const modal = document.querySelector<HTMLElement>('[data-onboarding="modal-client-form"]');
      if (!modal) return;
      const rect = modal.getBoundingClientRect();
      if (rect.width === 0) return;
      ensureSpotlightStyles();
      const stepNumber = WIZARD_STEPS.findIndex(s => s.id === 'create-client') + 1;
      setSpotlight({
        stepNumber,
        hint: 'Llena todos los campos y da clic en Crear cliente 👤',
        targetSelector: '[data-onboarding="modal-client-form"]',
        bubblePosition: 'left-of-modal',
        targetRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height },
      });
    };

    const observer = new MutationObserver(tryClientModalSpotlight);
    observer.observe(document.body, { childList: true, subtree: true });
    tryClientModalSpotlight();
    return () => observer.disconnect();
  }, [
    progress.currentStep?.id,
    progress.subItemStatus.hasClients,
    minimized,
    progress.isActive,
    location.pathname,
  ]);

  // ── Sound + smart navigation on sub-item completion ──────────────────────
  useEffect(() => {
    if (!progress.isActive) { prevSubItemRef.current = null; return; }
    const curr = progress.subItemStatus;
    if (!prevSubItemRef.current) { prevSubItemRef.current = { ...curr }; return; }
    const prev = prevSubItemRef.current;
    const anyNew = (Object.keys(curr) as (keyof SubItemStatus)[]).some(k => !prev[k] && curr[k]);
    if (anyNew) {
      playSubItemSound();
      // Dirección completada → navegar a Horarios de Apertura
      if (!prev.salonAddress && curr.salonAddress) {
        navigate('/settings?section=hours');
      }
    }
    prevSubItemRef.current = { ...curr };
  }, [progress.subItemStatus, progress.isActive, navigate]);

  // ── Resize-aware spotlight: update rect when spotlighted element changes size ──
  useEffect(() => {
    if (!spotlight?.targetSelector) return;
    const el = document.querySelector<HTMLElement>(spotlight.targetSelector);
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setSpotlight(prev => prev ? {
        ...prev,
        targetRect: {
          top: rect.top, bottom: rect.bottom,
          left: rect.left, right: rect.right,
          width: rect.width, height: rect.height,
        },
      } : null);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [spotlight?.targetSelector]);

  // ── Backfill effect ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!progress.salonId || !progress.needsRequirementBackfill || backfillRef.current) return;
    backfillRef.current = true;
    void (async () => {
      try {
        const { data: salon, error: salonError } = await supabase
          .from('salons').select('settings').eq('id', progress.salonId).maybeSingle();
        if (salonError) throw salonError;
        const cur = isRecord(salon?.settings) ? salon.settings : {};
        const { error } = await supabase.from('salons')
          .update({ settings: { ...cur, onboarding_v2_required: true, onboarding_v2_completed_at: cur.onboarding_v2_completed_at ?? null } })
          .eq('id', progress.salonId);
        if (error) throw error;
        await queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      } catch (err) {
        logger.error('[OnboardingWizard] backfill failed', err);
        backfillRef.current = false;
      }
    })();
  }, [progress.needsRequirementBackfill, progress.salonId, queryClient]);

  // ── Finalize effect ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!progress.shouldFinalize || !progress.salonId || finalizingRef.current) return;
    finalizingRef.current = true;
    void (async () => {
      try {
        const { data: salon, error: salonError } = await supabase
          .from('salons').select('settings').eq('id', progress.salonId).maybeSingle();
        if (salonError) throw salonError;
        const cur = isRecord(salon?.settings) ? salon.settings : {};
        const { error } = await supabase.from('salons')
          .update({ settings: { ...cur, onboarding_v2_completed_at: new Date().toISOString() } })
          .eq('id', progress.salonId);
        if (error) throw error;
        // Ding de celebración final + modal
        playSubItemSound();
        setShowCelebration(true);
        await queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      } catch (err) {
        logger.error('[OnboardingWizard] finalize failed', err);
        finalizingRef.current = false;
      }
    })();
  }, [progress.salonId, progress.shouldFinalize, queryClient]);

  // ── Step transition toast ─────────────────────────────────────────────────
  useEffect(() => {
    if (!progress.isActive) { previousCurrentStepIdRef.current = progress.currentStep?.id ?? null; return; }
    const prev = previousCurrentStepIdRef.current;
    const curr = progress.currentStep?.id ?? null;
    if (prev && curr && prev !== curr) {
      const done = progress.steps.find(s => s.id === prev);
      const next = progress.steps.find(s => s.id === curr);
      if (done?.isComplete && next) {
        toast.success(`${done.label} listo. Siguiente: ${next.label.toLowerCase()}`);
        setMinimized(false);
        setExpandedStepId(curr);
      }
    }
    previousCurrentStepIdRef.current = curr;
  }, [progress.currentStep, progress.isActive, progress.steps]);

  // Si el onboarding ya terminó pero el modal de celebración aún está abierto,
  // renderizamos SOLO el modal (no el panel) hasta que el usuario lo cierre.
  if (!progress.isActive) {
    if (!showCelebration) return null;
    return (
      <OnboardingCelebrationModal
        open={showCelebration}
        onClose={() => setShowCelebration(false)}
      />
    );
  }

  // ── Handler "Empezar": marca onboarding como iniciado en DB y navega al paso actual ──
  const handleStart = async () => {
    if (!progress.salonId) return;
    try {
      const { data: salon } = await supabase
        .from('salons').select('settings').eq('id', progress.salonId).maybeSingle();
      const cur = (salon?.settings && typeof salon.settings === 'object' && !Array.isArray(salon.settings))
        ? salon.settings as Record<string, unknown>
        : {};
      await supabase.from('salons')
        .update({ settings: { ...cur, onboarding_v2_started: true } })
        .eq('id', progress.salonId);
      await queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      // Navegar al paso actual
      if (progress.currentStep) {
        navigate(progress.currentStep.route, { replace: true });
      }
    } catch (err) {
      logger.error('[OnboardingWizard] handleStart failed', err);
    }
  };

  // Mostrar welcome modal si el usuario aún no ha iniciado el onboarding
  if (!progress.isStarted) {
    return (
      <OnboardingWelcomeModal
        open
        onStart={handleStart}
      />
    );
  }

  const visibleSteps = WIZARD_STEPS.filter(ws => progress.steps.some(s => s.id === ws.id));
  const currentStepId = progress.currentStep?.id ?? null;

  const toggleExpand = (id: OnboardingStepId) =>
    setExpandedStepId(prev => prev === id ? null : id);

  // ── Minimized pill ────────────────────────────────────────────────────────
  if (minimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[60]">
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="flex items-center gap-3 rounded-full bg-gradient-to-r from-[#0f7a8a] to-[#58BDB3] px-5 py-3 shadow-xl text-white transition-all hover:shadow-2xl hover:scale-105 active:scale-100"
        >
          <CheckSquare className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-semibold">
            Paso {progress.completedCount + 1} de {progress.totalCount}
          </span>
          <div className="h-1.5 w-16 rounded-full bg-white/30 overflow-hidden">
            <div
              className="h-full rounded-full bg-white transition-[width] duration-500"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </button>
      </div>
    );
  }

  // ── Spotlight geometry ────────────────────────────────────────────────────
  const sp = spotlight?.targetRect ?? null;
  const PAD = 6; // px breathing room around target

  return (
    <>
      {/* ── 4-rect overlay: z-[48] covers everything (incl. header z-40) ── */}
      {/* No onClick — overlay is permanent until Minimizar is clicked.     */}
      {/* Top/bottom strips also extend right: 320 to stop before panel.   */}
      {sp && (
        <>
          {/* Top strip */}
          <div
            className="fixed z-[48] bg-black/60"
            style={{ top: 0, left: 0, right: 320, height: Math.max(0, sp.top - PAD) }}
          />
          {/* Bottom strip */}
          <div
            className="fixed z-[48] bg-black/60"
            style={{ top: sp.bottom + PAD, left: 0, right: 320, bottom: 0 }}
          />
          {/* Left strip */}
          <div
            className="fixed z-[48] bg-black/60"
            style={{
              top: sp.top - PAD,
              left: 0,
              width: Math.max(0, sp.left - PAD),
              height: sp.height + PAD * 2,
            }}
          />
          {/* Right strip (stop before the panel) */}
          <div
            className="fixed z-[48] bg-black/60"
            style={{
              top: sp.top - PAD,
              left: sp.right + PAD,
              right: 320,
              height: sp.height + PAD * 2,
            }}
          />
        </>
      )}

      {/* ── Speech bubble: z-[49] (in-page/nav) or z-[54] (left-of-modal) ── */}
      {spotlight && sp && (() => {
        const pos = spotlight.bubblePosition;

        let bubbleStyle: React.CSSProperties;
        let triangleStyle: React.CSSProperties;

        if (pos === 'left-of-modal') {
          // Alinear verticalmente al campo activo dentro del modal
          const modalStep = document.querySelector<HTMLElement>('[data-modal-step]')?.getAttribute('data-modal-step');
          const innerSelector = modalStep === 'services'
            ? '[data-onboarding="field-professional-services"]'
            : '[data-onboarding="field-professional-name"], [data-onboarding="field-client-name"]';
          const innerEl = document.querySelector<HTMLElement>(innerSelector);
          const innerRect = innerEl?.getBoundingClientRect();
          const bubbleTopTarget = innerRect && innerRect.width > 0
            ? innerRect.top + innerRect.height / 2
            : sp.top + Math.min(sp.height * 0.25, 80);
          bubbleStyle = {
            position: 'fixed',
            top: bubbleTopTarget,
            transform: 'translateY(-50%)',
            right: window.innerWidth - sp.left + PAD + 14,
            zIndex: 54,
            pointerEvents: 'none',
          };
          triangleStyle = {
            position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)',
            width: 0, height: 0,
            borderTop: '8px solid transparent', borderBottom: '8px solid transparent',
            borderLeft: '8px solid #fff',
          };
        } else if (pos === 'left-of-panel') {
          // Burbuja a la derecha del nav item spotlighteado, al mismo nivel vertical
          bubbleStyle = {
            position: 'fixed',
            top: sp.top + sp.height / 2,
            transform: 'translateY(-50%)',
            left: sp.right + 14,
            zIndex: 51,
            pointerEvents: 'none',
          };
          triangleStyle = {
            position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)',
            width: 0, height: 0,
            borderTop: '8px solid transparent', borderBottom: '8px solid transparent',
            borderRight: '8px solid #fff',
          };
        } else if (pos === 'below') {
          // Centra la burbuja exactamente bajo el elemento; clamp suave para no salirse de pantalla
          const bubbleW = 180;
          const centerX = sp.left + sp.width / 2;
          const clampedLeft = Math.min(Math.max(centerX, bubbleW / 2 + 8), window.innerWidth - 320 - bubbleW / 2 - 8);
          bubbleStyle = {
            position: 'fixed',
            top: sp.bottom + PAD + 10,
            left: clampedLeft,
            transform: 'translateX(-50%)',
            zIndex: 49,
            pointerEvents: 'none',
          };
          triangleStyle = {
            position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
            borderBottom: '8px solid #fff',
          };
        } else {
          // 'right' — default in-page, may flip left if not enough space
          const spaceRight = (window.innerWidth - 320) - sp.right - PAD;
          const goLeft = spaceRight < 250;
          bubbleStyle = {
            position: 'fixed',
            top: sp.top + sp.height / 2,
            ...(goLeft
              ? { right: window.innerWidth - sp.left + PAD + 14 }
              : { left: sp.right + PAD + 14 }),
            transform: 'translateY(-50%)',
            zIndex: 49,
            pointerEvents: 'none',
          };
          triangleStyle = goLeft
            ? {
                position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)',
                width: 0, height: 0,
                borderTop: '8px solid transparent', borderBottom: '8px solid transparent',
                borderLeft: '8px solid #fff',
              }
            : {
                position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)',
                width: 0, height: 0,
                borderTop: '8px solid transparent', borderBottom: '8px solid transparent',
                borderRight: '8px solid #fff',
              };
        }

        return (
          <div style={bubbleStyle}>
            <div style={triangleStyle} />
            <div
              style={{
                background: '#fff',
                borderRadius: 14,
                padding: '10px 18px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: '#14b8a6',
                maxWidth: pos === 'below' ? 180 : 220,
                textAlign: 'center',
                lineHeight: 1.45,
              }}
            >
              {spotlight.hint}
            </div>
          </div>
        );
      })()}

      {/* ── PASO N ▶ — floating to the LEFT of the panel, aligned with step row ── */}
      {spotlight && (() => {
        const stepId = progress.currentStep?.id ?? '';
        const rowEl  = stepRowRefs.current[stepId];
        const rowRect = rowEl?.getBoundingClientRect();
        const topPx   = rowRect
          ? rowRect.top + rowRect.height / 2 - 16  // center on step row
          : 14;
        return (
        <div
          style={{
            position: 'fixed',
            top: topPx,
            right: 328,
            zIndex: 51,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 20,
              fontWeight: 900,
              color: '#8df7ff',
              letterSpacing: '-0.5px',
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
            }}
          >
            PASO {spotlight.stepNumber}
          </span>
          <span
            style={{
              display: 'inline-block',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 22,
              fontWeight: 900,
              color: '#8df7ff',
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
              animation: '_ms_paso_bounce 0.55s ease-in-out infinite',
            }}
          >
            ▶
          </span>
        </div>
        );
      })()}

      {/* ── Side panel: z-50 (always above overlay) ── */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-[320px] flex flex-col bg-white border-l border-slate-200 shadow-2xl">

        {/* Panel header */}
        <div className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <img src="/ISO_TIPOMESTLY.png" alt="Mestly" className="h-5 w-5 object-contain" />
              <span className="text-sm font-bold text-slate-800 tracking-tight">Configuración inicial</span>
            </div>
            <button
              type="button"
              onClick={() => setMinimized(true)}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <Minimize2 className="h-3.5 w-3.5" />
              <span>Minimizar</span>
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-[11px] text-slate-400">{progress.completedCount} de {progress.totalCount} pasos</span>
              <span className="text-[11px] font-semibold text-[#2db1c3]">{progress.percentage}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#2db1c3] to-[#58BDB3] transition-[width] duration-500"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Steps accordion */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
          {visibleSteps.map((stepDef, index) => {
            const progressStep = progress.steps.find(s => s.id === stepDef.id);
            const isComplete   = progressStep?.isComplete ?? false;
            const isCurrent    = stepDef.id === currentStepId;
            const isExpanded   = expandedStepId === stepDef.id;
            const isAccessible = progressStep?.isAccessible ?? false;
            const Icon         = stepDef.icon;

            return (
              <div
                key={stepDef.id}
                className={cn(
                  'rounded-2xl border transition-all duration-200 overflow-hidden',
                  isComplete && 'border-emerald-100 bg-emerald-50/50',
                  isCurrent && !isComplete && 'border-[#58BDB3]/30 bg-[#f0fafa]',
                  !isCurrent && !isComplete && 'border-slate-100 bg-slate-50/60',
                )}
              >
                {/* Step header row */}
                <button
                  ref={el => { stepRowRefs.current[stepDef.id] = el; }}
                  type="button"
                  disabled={!isAccessible && !isComplete}
                  onClick={() => (isAccessible || isComplete) ? toggleExpand(stepDef.id) : undefined}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                    (isAccessible || isComplete) && 'hover:bg-black/[0.02]',
                    (!isAccessible && !isComplete) && 'cursor-not-allowed opacity-40',
                  )}
                >
                  <div className={cn(
                    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold',
                    isComplete && 'bg-emerald-500 text-white',
                    isCurrent && !isComplete && 'bg-[#2db1c3] text-white',
                    !isCurrent && !isComplete && 'bg-slate-200 text-slate-500',
                  )}>
                    {isComplete ? <Check className="h-3.5 w-3.5" /> : <span>{index + 1}</span>}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon className={cn(
                        'h-3.5 w-3.5 flex-shrink-0',
                        isComplete ? 'text-emerald-500' : isCurrent ? 'text-[#2db1c3]' : 'text-slate-400',
                      )} />
                      <p className={cn(
                        'text-sm truncate',
                        isComplete && 'text-emerald-700 font-medium',
                        isCurrent && !isComplete && 'text-slate-900 font-semibold',
                        !isCurrent && !isComplete && 'text-slate-500 font-medium',
                      )}>
                        {stepDef.label}
                      </p>
                    </div>
                  </div>

                  {(isAccessible || isComplete) && (
                    isExpanded
                      ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  )}
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-4 pb-4">
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">{stepDef.description}</p>

                    {stepDef.subItems.length > 0 && (() => {
                      // Deduplicar por key: cuando varios sub-items comparten key
                      // (p.ej. modal + botón del mismo paso), mostrar solo uno en el
                      // checklist. Guardamos la última ocurrencia de cada key porque
                      // tiene el label más descriptivo (el del botón, no el del modal).
                      const seen = new Set<string>();
                      const uniqueItems = [...stepDef.subItems].reverse().filter(si => {
                        if (seen.has(si.key)) return false;
                        seen.add(si.key);
                        return true;
                      }).reverse();

                      return (
                        <div className="space-y-2 mb-3">
                          {uniqueItems.map((subItem) => {
                            const isDone = progress.subItemStatus[subItem.key];
                            return (
                              <div key={subItem.key} className="flex items-center gap-2.5">
                                <div
                                  className={cn(
                                    'flex flex-shrink-0 items-center justify-center rounded-full',
                                    isDone ? 'bg-emerald-100' : 'bg-white border border-slate-200',
                                  )}
                                  style={{ width: 18, height: 18 }}
                                >
                                  {isDone
                                    ? <Check className="h-2.5 w-2.5 text-emerald-600" />
                                    : <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                                  }
                                </div>
                                <span className={cn('text-xs', isDone ? 'text-slate-400 line-through' : 'text-slate-700')}>
                                  {subItem.label}
                                  {subItem.optional && (
                                    <span className="ml-1 text-[10px] text-slate-400">(opcional)</span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {isComplete ? (
                      <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                        <Check className="h-3.5 w-3.5" />
                        Completado
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Modal de celebración al completar el onboarding ── */}
      <OnboardingCelebrationModal
        open={showCelebration}
        onClose={() => setShowCelebration(false)}
      />
    </>
  );
}
